// Passport parser. Reads the Machine Readable Zone (MRZ) from the bottom
// of a passport biographic page or visa page. Two MRZ standards:
//
//   TD3 (passport, 2 lines x 44 chars)
//   TD1 (ID card, 3 lines x 30 chars)
//
// Indian passports use TD3. We parse the two MRZ lines, validate the
// checksums, and emit a structured result. The integrating app pulls the
// MRZ text via Adobe PDF MCP extract_pdf and feeds it here.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok, shortHash } from './_types.ts';

export interface PassportInfo {
  readonly documentType: 'P' | 'V'; // P=passport, V=visa
  readonly issuingCountry: string; // ISO-3 e.g. IND
  readonly surname: string;
  readonly givenNames: string;
  readonly passportNumber: string;
  readonly passportNumberValid: boolean;
  readonly nationality: string;
  readonly dob: string; // yyyy-mm-dd
  readonly dobValid: boolean;
  readonly sex: 'M' | 'F' | 'X';
  readonly expiryDate: string;
  readonly expiryDateValid: boolean;
  readonly personalNumber: string;
  readonly personalNumberValid: boolean;
  readonly compositeValid: boolean;
}

export interface PassportConnectorStatus extends ConnectorStatus {
  readonly id: 'passport-parser';
}

interface PassportPersist {
  readonly connectedAt: string;
  readonly passportNumberHash: string;
  readonly nationality: string;
  readonly expiryDate: string;
}

const STORE_KEY = 'empire/connectors/passport-parser';

export class PassportParserAdapter
  implements ConnectorAdapter<PassportConnectorStatus, PassportInfo>
{
  readonly id = 'passport-parser';
  readonly displayName = 'Passport MRZ parser';
  readonly displayNameHi = 'पासपोर्ट एमआरज़ेड पार्सर';
  readonly dpdpCategory = 'identity-passport-mrz';
  readonly mode: ConnectorMode;
  #pendingMrz: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<PassportConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.passportNumberHash ?? null,
      notes: 'TD3 MRZ parser, all check digits validated. Pair with Adobe PDF MCP for biographic-page text.',
    };
  }

  async connect(): Promise<ConnectorResult<PassportConnectorStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  queueMrz(mrz: string): void {
    this.#pendingMrz = mrz;
  }

  async importPayload(): Promise<ConnectorResult<PassportInfo>> {
    if (!this.#pendingMrz) {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    const parsed = parseTd3Mrz(this.#pendingMrz);
    if (!parsed) {
      return err({ code: 'parse', message: 'MRZ did not match TD3 layout.', retriable: false });
    }
    await this.#persist(parsed);
    return ok(parsed);
  }

  mockPayload(): PassportInfo {
    return {
      documentType: 'P',
      issuingCountry: 'IND',
      surname: 'SHARMA',
      givenNames: 'ADITYA',
      passportNumber: 'M1234567',
      passportNumberValid: true,
      nationality: 'IND',
      dob: '1992-04-15',
      dobValid: true,
      sex: 'M',
      expiryDate: '2032-08-19',
      expiryDateValid: true,
      personalNumber: '',
      personalNumberValid: true,
      compositeValid: true,
    };
  }

  async #persist(p: PassportInfo): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const rec: PassportPersist = {
      connectedAt: nowIso(),
      passportNumberHash: await shortHash(p.passportNumber),
      nationality: p.nationality,
      expiryDate: p.expiryDate,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): PassportPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as PassportPersist; } catch { return null; }
}

// ─────────────── TD3 parser ───────────────

const TD3_LINE_LEN = 44;

export function parseTd3Mrz(input: string): PassportInfo | null {
  const lines = input.replace(/\s+/g, '\n').split('\n').filter((l) => l.length >= TD3_LINE_LEN - 2);
  if (lines.length < 2) return null;
  const line1 = padTo44(lines[0] ?? '');
  const line2 = padTo44(lines[1] ?? '');
  if (line1.length !== TD3_LINE_LEN || line2.length !== TD3_LINE_LEN) return null;

  const documentType = (line1[0] === 'V' ? 'V' : 'P');
  const issuingCountry = line1.slice(2, 5);
  const nameField = line1.slice(5, TD3_LINE_LEN);
  const namesSplit = nameField.split('<<');
  const surname = (namesSplit[0] ?? '').replace(/</g, ' ').trim();
  const givenNames = (namesSplit[1] ?? '').replace(/</g, ' ').trim();

  const passportNumber = stripFiller(line2.slice(0, 9));
  const passportCheck = line2[9];
  const passportNumberValid = mrzCheckDigit(line2.slice(0, 9)) === passportCheck;

  const nationality = line2.slice(10, 13);
  const dobRaw = line2.slice(13, 19);
  const dobCheck = line2[19];
  const dobValid = mrzCheckDigit(dobRaw) === dobCheck;
  const dob = mrzDate(dobRaw, true);

  const sex = (line2[20] === 'F' ? 'F' : line2[20] === 'M' ? 'M' : 'X');
  const expiryRaw = line2.slice(21, 27);
  const expiryCheck = line2[27];
  const expiryDateValid = mrzCheckDigit(expiryRaw) === expiryCheck;
  const expiryDate = mrzDate(expiryRaw, false);

  const personalNumber = stripFiller(line2.slice(28, 42));
  const personalNumberCheck = line2[42];
  const personalNumberValid = personalNumber === '' || mrzCheckDigit(line2.slice(28, 42)) === personalNumberCheck;

  const composite = line2.slice(0, 10) + line2.slice(13, 20) + line2.slice(21, 28) + line2.slice(28, 43);
  const compositeCheck = line2[43];
  const compositeValid = mrzCheckDigit(composite) === compositeCheck;

  return {
    documentType,
    issuingCountry,
    surname,
    givenNames,
    passportNumber,
    passportNumberValid,
    nationality,
    dob,
    dobValid,
    sex,
    expiryDate,
    expiryDateValid,
    personalNumber,
    personalNumberValid,
    compositeValid,
  };
}

function padTo44(line: string): string {
  return line.padEnd(TD3_LINE_LEN, '<').slice(0, TD3_LINE_LEN);
}

function stripFiller(s: string): string {
  return s.replace(/</g, '').trim();
}

function mrzDate(yymmdd: string, isDob: boolean): string {
  if (yymmdd.length !== 6) return '';
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  if (Number.isNaN(yy)) return '';
  const currentYear = new Date().getFullYear();
  const currentYY = currentYear % 100;
  let yyyy: number;
  if (isDob) {
    yyyy = yy <= currentYY ? 2000 + yy : 1900 + yy;
  } else {
    yyyy = yy < 80 ? 2000 + yy : 1900 + yy;
  }
  return `${yyyy}-${mm}-${dd}`;
}

const MRZ_WEIGHTS = [7, 3, 1];

function mrzCheckDigit(field: string): string {
  let sum = 0;
  for (let i = 0; i < field.length; i++) {
    const ch = field[i] ?? '<';
    const v = mrzCharValue(ch);
    const w = MRZ_WEIGHTS[i % 3] ?? 1;
    sum += v * w;
  }
  return String(sum % 10);
}

function mrzCharValue(ch: string): number {
  if (ch === '<') return 0;
  if (ch >= '0' && ch <= '9') return ch.charCodeAt(0) - 48;
  if (ch >= 'A' && ch <= 'Z') return ch.charCodeAt(0) - 55;
  return 0;
}

export function makePassportParser(mode: ConnectorMode): PassportParserAdapter {
  return new PassportParserAdapter(mode);
}
